import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Phone, Mail, X, KeyRound } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nj-liquor-delivery/artifacts/q14l1b4u_1001594216.jpg";

export default function AuthPage({ mode = 'login' }) {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loginType, setLoginType] = useState('phone'); // phone (customer) or email (admin/driver/staff)
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [showForgot, setShowForgot] = useState(false);

  // Deep-link support: /login?forgot=1 auto-opens the Forgot Password modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('forgot') === '1') {
      setShowForgot(true);
      setLoginType('email');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        const loginData = loginType === 'phone'
          ? { phone, password }
          : { email, password };
        const data = await login(loginData);
        toast.success(`Welcome back, ${data.name || 'User'}!`);
        if (data.role === 'admin' || data.role === 'super_admin') navigate('/admin');
        else if (data.role === 'driver') navigate('/driver');
        else if (data.role === 'staff') navigate('/staff');
        else navigate('/shop');
      } else {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        if (!phone.trim()) { setError('Phone number is required'); setLoading(false); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters'); setLoading(false); return; }
        const data = await register(name, phone, password, email);
        toast.success('Account created!');
        navigate('/shop');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Hoboken LGD" className="w-14 h-14 rounded-xl object-cover" />
          <div className="text-center">
            <span className="font-['Playfair_Display'] text-lg font-medium text-white block">HOBOKEN LIQUOR</span>
            <span className="font-['Playfair_Display'] text-xs text-[#94A3B8] tracking-wider">GROCERY & DELI</span>
          </div>
        </Link>

        <div className="bg-[#12151C] border border-white/10 rounded-2xl p-8 animate-fade-in">
          {/* Login / Register Toggle */}
          <div className="flex gap-0 mb-6 bg-[#0B0E14] rounded-full p-1">
            <button onClick={() => { setIsLogin(true); setError(''); }} className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all ${isLogin ? 'bg-[#cc3737] text-white' : 'text-[#64748B]'}`} data-testid="login-tab">Sign In</button>
            <button onClick={() => { setIsLogin(false); setError(''); }} className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all ${!isLogin ? 'bg-[#cc3737] text-white' : 'text-[#64748B]'}`} data-testid="register-tab">Sign Up</button>
          </div>

          {/* Login Type Toggle (only for login) */}
          {isLogin && (
            <div className="flex gap-2 mb-6">
              <button onClick={() => setLoginType('phone')} className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${loginType === 'phone' ? 'bg-[#b56b38]/20 text-[#b56b38] border border-[#b56b38]/30' : 'text-[#64748B] border border-white/5'}`} data-testid="login-type-phone">
                <Phone className="w-3.5 h-3.5" /> Customer (Phone)
              </button>
              <button onClick={() => setLoginType('email')} className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${loginType === 'email' ? 'bg-[#b56b38]/20 text-[#b56b38] border border-[#b56b38]/30' : 'text-[#64748B] border border-white/5'}`} data-testid="login-type-email">
                <Mail className="w-3.5 h-3.5" /> Admin / Staff / Driver
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Registration fields */}
            {!isLogin && (
              <>
                <div>
                  <label className="text-[#94A3B8] text-sm mb-1.5 block">Full Name *</label>
                  <input value={name} onChange={e => setName(e.target.value)} type="text" placeholder="Your full name" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="name-input" />
                </div>
                <div>
                  <label className="text-[#94A3B8] text-sm mb-1.5 block">Phone Number *</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} type="tel" placeholder="(201) 555-0000" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="phone-input" />
                </div>
                <div>
                  <label className="text-[#94A3B8] text-sm mb-1.5 block">Email (optional)</label>
                  <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="email-input" />
                </div>
              </>
            )}

            {/* Login fields */}
            {isLogin && loginType === 'phone' && (
              <div>
                <label className="text-[#94A3B8] text-sm mb-1.5 block">Phone Number</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} type="tel" placeholder="Enter your phone number" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="phone-input" />
              </div>
            )}
            {isLogin && loginType === 'email' && (
              <div>
                <label className="text-[#94A3B8] text-sm mb-1.5 block">Email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@hobokenlgd.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="email-input" />
              </div>
            )}

            <div>
              <label className="text-[#94A3B8] text-sm mb-1.5 block">Password</label>
              <div className="relative">
                <input value={password} onChange={e => setPassword(e.target.value)} type={showPass ? 'text' : 'password'} placeholder={!isLogin ? 'Create password (min 6 chars)' : 'Enter password'} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38] pr-10" data-testid="password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && <p className="text-[#EF4444] text-sm" data-testid="auth-error">{error}</p>}

            <button type="submit" disabled={loading} className="w-full bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full py-3 font-medium transition-colors" data-testid="auth-submit-btn">
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>

            {isLogin && (
              <button type="button" onClick={() => setShowForgot(true)} className="w-full text-[#b56b38] hover:text-[#d8964f] text-sm transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </button>
            )}
          </form>

          <p className="text-center text-[#64748B] text-sm mt-6">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-[#b56b38] hover:underline">
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} defaultType={loginType} />}
    </div>
  );
}

function ForgotPasswordModal({ onClose, defaultType = 'phone' }) {
  const [step, setStep] = useState(1); // 1 = request code, 2 = enter code + new password
  const [type, setType] = useState(defaultType); // 'phone' or 'email'
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return setError('Enter phone or email');
    setError(''); setLoading(true);
    try {
      const payload = type === 'phone' ? { phone: identifier.trim() } : { email: identifier.trim().toLowerCase() };
      await api.post('/auth/forgot-password', payload);
      toast.success('If the account exists, a reset code has been sent.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to request reset');
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!code.trim()) return setError('Enter the 6-digit code');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    setError(''); setLoading(true);
    try {
      const payload = {
        code: code.trim(),
        new_password: newPassword,
        ...(type === 'phone' ? { phone: identifier.trim() } : { email: identifier.trim().toLowerCase() }),
      };
      await api.post('/auth/reset-password', payload);
      toast.success('Password reset! You can now sign in.');
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="forgot-password-modal">
      <div className="bg-[#12151C] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Playfair_Display'] text-xl text-white flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#b56b38]" /> Reset Password</h3>
          <button onClick={onClose} className="text-[#64748B] hover:text-white" data-testid="close-forgot-modal"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 ? (
          <form onSubmit={requestCode} className="space-y-3">
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setType('phone')} className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${type === 'phone' ? 'bg-[#b56b38]/20 text-[#b56b38] border border-[#b56b38]/30' : 'text-[#64748B] border border-white/5'}`} data-testid="forgot-type-phone"><Phone className="w-3.5 h-3.5" /> Phone</button>
              <button type="button" onClick={() => setType('email')} className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${type === 'email' ? 'bg-[#b56b38]/20 text-[#b56b38] border border-[#b56b38]/30' : 'text-[#64748B] border border-white/5'}`} data-testid="forgot-type-email"><Mail className="w-3.5 h-3.5" /> Email</button>
            </div>
            <div>
              <label className="text-[#94A3B8] text-sm mb-1.5 block">{type === 'phone' ? 'Phone Number' : 'Email Address'}</label>
              <input value={identifier} onChange={e => setIdentifier(e.target.value)} type={type === 'phone' ? 'tel' : 'email'} placeholder={type === 'phone' ? '(201) 555-0000' : 'you@example.com'} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="forgot-identifier-input" />
            </div>
            {error && <p className="text-[#EF4444] text-sm" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full py-2.5 font-medium" data-testid="request-reset-code-btn">{loading ? 'Please wait...' : 'Send Reset Code'}</button>
          </form>
        ) : (
          <form onSubmit={submitReset} className="space-y-3">
            <p className="text-[#94A3B8] text-xs">Enter the 6-digit code you received and choose a new password.</p>
            <div>
              <label className="text-[#94A3B8] text-sm mb-1.5 block">6-Digit Code</label>
              <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-center text-lg font-mono tracking-[0.3em] focus:outline-none focus:border-[#b56b38]" data-testid="reset-code-input" />
            </div>
            <div>
              <label className="text-[#94A3B8] text-sm mb-1.5 block">New Password</label>
              <input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" placeholder="Min 6 characters" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="new-password-input" />
            </div>
            <div>
              <label className="text-[#94A3B8] text-sm mb-1.5 block">Confirm Password</label>
              <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" placeholder="Retype new password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-white placeholder:text-[#64748B] text-sm focus:outline-none focus:border-[#b56b38]" data-testid="confirm-password-input" />
            </div>
            {error && <p className="text-[#EF4444] text-sm" data-testid="forgot-error">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStep(1); setCode(''); setError(''); }} className="flex-1 border border-white/10 text-[#94A3B8] rounded-full py-2.5 text-sm" data-testid="back-step-btn">Back</button>
              <button type="submit" disabled={loading} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full py-2.5 font-medium" data-testid="submit-reset-btn">{loading ? 'Resetting...' : 'Reset Password'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
